/* Cart progress bar (snippets/lf-cart-progress.liquid).

   The snippet owns the copy and the bar width, and the drawer section is re-rendered
   after every cart change, so this file never draws the bar. It keeps the gift line
   in step with the rules the snippet publishes on data-gift-mode:
   - auto  (signed in, not Pro, never ordered the gift product): add one gift line at
     the threshold, remove it below.
   - offer (not signed in, or the snippet could not read the whole order history): the
     shopper adds it with the button; removed below the threshold.
   - none  (Pro Member, ordered the gift product before, no gift set): any gift line is
     removed, because the discount that makes it free will not apply to them.

   Never loops. An automatic add is tried once per cart contents (the non-gift lines),
   so a shopper who deletes the gift keeps it deleted until they change the cart, and a
   failed add is not retried until then either. A hard cap per page view backs that up.

   Cart writes use fetch, never a form submit, so Rebuy's form listener does not pick
   them up, and each one dispatches the theme's cart update event so the drawer redraws. */
import { CartLinesUpdateEvent } from '@shopify/events';

const SECTION = 'cart-drawer-section';
const GIFT_PROP = '_lf_gift';
const ATTEMPT_KEY = 'lf-cart-gift-attempt';
const MAX_AUTO_ADDS = 3;

const routeRoot = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
const bar = () => document.querySelector('[data-lf-cart-progress]');

let memoryAttempt = null;
function readAttempt() {
  try {
    return sessionStorage.getItem(ATTEMPT_KEY) ?? memoryAttempt;
  } catch {
    return memoryAttempt;
  }
}
function writeAttempt(value) {
  memoryAttempt = value;
  try {
    sessionStorage.setItem(ATTEMPT_KEY, value);
  } catch {
    /* private mode or blocked storage: the in-memory copy covers this page view */
  }
}

const isGift = (item) => item.properties != null && item.properties[GIFT_PROP] != null && item.properties[GIFT_PROP] !== '';

function signature(cart) {
  return cart.items
    .filter((item) => !isGift(item))
    .map((item) => item.variant_id + 'x' + item.quantity)
    .sort()
    .join(',');
}

async function getCart() {
  const res = await fetch(routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('Could not read the cart');
  return res.json();
}

async function post(path, body) {
  const res = await fetch(routeRoot() + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ...body, sections: SECTION }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.description || json.message || 'That could not be added to your cart.');
  return json;
}

// Events this file dispatched. Its own listener skips them, since the cart they
// describe is the one it just wrote.
const ownEvents = new WeakSet();

// Same handshake the product form and the quick order use: dispatch a
// CartLinesUpdateEvent whose promise carries the cart and the re-rendered drawer
// section, which cart-items-component morphs in.
async function tellTheme(action, lines, response) {
  const deferred = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise });
  ownEvents.add(event);
  (bar() || document.body).dispatchEvent(event);
  try {
    const cart = typeof response.total_price === 'number' ? response : await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: response.sections || {}, source: 'lf-cart-progress' },
    });
  } catch (error) {
    deferred.reject(error);
  }
}

async function addGift(variantId) {
  const out = await post('cart/add.js', {
    items: [{ id: Number(variantId), quantity: 1, properties: { [GIFT_PROP]: 'true' } }],
  });
  await tellTheme('add', [{ merchandiseId: String(variantId), quantity: 1 }], out);
}

async function updateGiftLines(updates) {
  const out = await post('cart/update.js', { updates });
  await tellTheme(
    'remove',
    Object.keys(updates).map((key) => ({ id: key, quantity: updates[key] })),
    out
  );
}

// Every cart write from this file runs one at a time, so a button add and an
// automatic check can never race each other into a double add.
let chain = Promise.resolve();
const exclusive = (task) => (chain = chain.then(task, task));

let autoAdds = 0;
let checkQueued = false;

async function check() {
  const el = bar();
  if (!el) return;
  const mode = el.dataset.giftMode;
  const variantId = el.dataset.giftVariant;
  const threshold = parseInt(el.dataset.giftThreshold, 10) || 0;
  const giftProduct = Number(el.dataset.giftProduct) || 0;
  const qualifying = el.dataset.giftProducts || '';

  const cart = await getCart();
  const gifts = cart.items.filter(isGift);
  // Same rule as the snippet: only qualifying products count, and no line of the
  // gift product counts, paid or free, because checkout leaves those out.
  const giftProgress = cart.items
    .filter((item) => !isGift(item) && item.product_id !== giftProduct)
    .filter((item) => !qualifying || qualifying.includes(',' + item.product_id + ','))
    .reduce((sum, item) => sum + item.final_line_price, 0);
  const met = mode !== 'none' && threshold > 0 && giftProgress >= threshold;

  if (gifts.length && !met) {
    const updates = {};
    gifts.forEach((item) => (updates[item.key] = 0));
    await updateGiftLines(updates);
  } else if (gifts.length > 1 || (gifts.length === 1 && gifts[0].quantity > 1)) {
    const updates = {};
    gifts.forEach((item, index) => (updates[item.key] = index === 0 ? 1 : 0));
    await updateGiftLines(updates);
  } else if (!gifts.length && met && mode === 'auto' && variantId && autoAdds < MAX_AUTO_ADDS) {
    const sig = signature(cart);
    if (readAttempt() !== sig) {
      writeAttempt(sig);
      autoAdds += 1;
      await addGift(variantId);
    }
  }
}

// Several cart events in a row collapse into one check that runs after the others.
function scheduleCheck() {
  if (checkQueued) return;
  checkQueued = true;
  exclusive(() => {
    checkQueued = false;
    return check().catch((error) => console.warn('[lf-cart-progress]', error));
  });
}

document.addEventListener(CartLinesUpdateEvent.eventName, (event) => {
  if (ownEvents.has(event)) return;
  const done = event.promise && typeof event.promise.then === 'function' ? event.promise : Promise.resolve();
  done.then(scheduleCheck, () => {});
});

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-lf-gift-add]') : null;
  if (!button || button.getAttribute('aria-busy') === 'true') return;
  const el = bar();
  if (!el || !el.dataset.giftVariant) return;

  const error = el.querySelector('[data-lf-gift-error]');
  const variantId = el.dataset.giftVariant;
  button.setAttribute('aria-busy', 'true');
  // Re-read the cart first: a second press that lands before the drawer redraws
  // must not add a second gift.
  await exclusive(async () => {
    try {
      const cart = await getCart();
      if (!cart.items.some(isGift)) await addGift(variantId);
    } catch (err) {
      if (error) {
        error.textContent = err.message;
        error.hidden = false;
      }
    }
  });
  button.removeAttribute('aria-busy');
});

if (bar()?.dataset.needsCheck === 'true') scheduleCheck();

// Free shipping celebration. Every cart change re-renders the drawer and the morph
// keeps this bar's element, so data-met switching on is the moment the cart crossed
// the threshold. A page that loads already over it never animates.
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const isMet = (el) => el.hasAttribute('data-met') && !el.hidden;
let wasMet = bar() ? isMet(bar()) : null;
let celebrateTimer = 0;

function watchMet() {
  const el = bar();
  if (!el) return;
  const met = isMet(el);
  if (met && wasMet === false && !reducedMotion.matches) {
    el.classList.remove('lf-cp--celebrate');
    void el.offsetWidth; // restart the animation if it is still running
    el.classList.add('lf-cp--celebrate');
    clearTimeout(celebrateTimer);
    celebrateTimer = setTimeout(() => bar()?.classList.remove('lf-cp--celebrate'), 700);
  }
  wasMet = met;
}

const drawerEl = document.getElementById('cart-drawer');
if (drawerEl) {
  new MutationObserver(watchMet).observe(drawerEl, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['data-met', 'hidden'],
  });
}
